import React from 'react';
import intl from "react-intl-universal";
import {observer} from "mobx-react-lite";
import { useAppState, useRootStore, useDsManager, useUserState } from "@stores/AppStateContext";
import {useCrudServices} from "../../../../platform/hooks/useServiceCombinations";

import {ContextMenu} from '../../../components/ContextMenu/ContextMenu';
import {createCluster} from '../../../../actions/item/createCluster';
import {createItem} from '../../../../actions/item/createItem';
import {logErrorToSentry} from '../../../../utils/errors/logErrorToSentry';
import {MODALS} from '../../../screens/modals/modal-names';

import {DownloadIcon} from '../../../components/icons/DownloadIcon';
import {AddIcon} from '../../../components/icons/AddIcon';
import {AddClusterIcon} from '../../../components/icons/AddClusterIcon';

export interface TreeQuickActionsContextMenuProps {
    handleClose: () => void;
    isDark?: boolean;
}

export const TreeQuickActionsContextMenu = observer(({ handleClose, isDark }: TreeQuickActionsContextMenuProps) => {
    const appState = useAppState();
    const rootStore = useRootStore();
    const userState = useUserState();
    const crudServices = useCrudServices();
    const workspace = rootStore.view.workspace!;
    const dsManager = useDsManager();
    const rootCreateParentId = workspace.view === 'main'
        ? workspace.selectedLibrary?.id ?? null
        : null;

    const doCreateItem = () => {
        if (!dsManager) {
            return;
        }
        createItem(crudServices, { workspace, appState, parentId: rootCreateParentId })
            .catch(logErrorToSentry)
            .finally(() => appState.setContextMenu(null));
    };

    const doCreateCluster = () => {
        if (!dsManager) {
            return;
        }
        createCluster(crudServices, { workspace, appState, parentId: rootCreateParentId })
            .catch(logErrorToSentry)
            .finally(() => appState.setContextMenu(null));
    };

    const doImport = () => {
        appState.setModal({ name: MODALS.IMPORT_DOC, params: undefined });
        appState.setContextMenu(null);
    };

    return (
        <ContextMenu
            isDark={isDark}
            handleClose={handleClose}
            items={[
                {
                    text: intl.get('tooltip.createPage').d('Create Page'),
                    Icon: AddIcon,
                    onClick: doCreateItem,
                    condition: !userState?.isCurrentUserReadOnly,
                },
                {
                    text: intl.get('tooltip.createCluster').d('Create Cluster'),
                    Icon: AddClusterIcon,
                    onClick: doCreateCluster,
                    condition: !userState?.isCurrentUserReadOnly,
                },
                {
                    text: intl.get('workspace_row.import_tooltip').d('Import'),
                    Icon: DownloadIcon,
                    onClick: doImport,
                    condition: !userState?.isCurrentUserReadOnly,
                },
            ]}
        />
    );
});
